/* eslint-disable camelcase */
import { pickBy } from 'lodash';

import { gaBridge } from 'client/utils/event-bridge';
import { appLog } from 'utils/logger';
import { location as UrlHelperLocation } from 'utils/url-helper';
import { parseGAParams } from 'utils/ga-params';
import { resolveCustomTrackerVariables } from 'utils/resolve-custom-tracker-variables';

export type GAEvent = {
	type: string;
	action: string;
	payload: { [key: string]: string | number | boolean };
};

/** Unified Media (VIDEO) block: GA action prefix and category. */
export type MediaAnalyticsKind = 'video' | 'audio';

export function mediaAnalyticsLabels(kind: MediaAnalyticsKind) {
	if (kind === 'audio') {
		return { noun: 'audio' as const, eventCategory: 'Audio interaction' as const };
	}
	return { noun: 'video' as const, eventCategory: 'Video interaction' as const };
}

type ValidRecord<T> = {
	[K in keyof T]: T[K] extends string | number | boolean ? T[K] : never;
};

const { isPreview } = UrlHelperLocation.client;
const log = appLog.extend('utils:analytics');

const pickValid = <T extends Record<string, any>>(o: T) =>
	pickBy(o, v => v !== null && v !== undefined && v !== '') as ValidRecord<T>;

/**
 */
/**
 * Note: Update EVENTS.md when adding, editing, or deleting API events
 *
 * @info https://developers.google.com/analytics/devguides/collection/gtagjs/events
 * 		 https://developers.google.com/tag-platform/gtagjs/reference/parameters
 */
async function track(eventName: string, { type, action, payload }: GAEvent) {
	try {
		if (isPreview) {
			return;
		}

		if (!window.gtag) {
			console.warn("gtag is't initialized");
			return;
		}

		const params = pickValid(payload);
		log('gtag', type, action, params);
		window.gtag(type, action, params);
		await gaBridge.sendEvent({ type: eventName, details: { type, action, payload: params } });
	} catch (error) {
		console.warn('Failed to track analytics event', error);
	}
}

export const analytics = {
	onLinkClick: (params: { storyId: string; cardId: string; name: string }) =>
		track('onLinkClick', {
			type: 'event',
			action: `lclick ${params.storyId}/${params.cardId}/${params.name}`,
			payload: {
				event_category: 'Link click',
				layer_name: params.name,
				story_id: params.storyId,
				card_id: params.cardId,
			},
		}),

	onApiRetry: (params: { attemptNumber: number; apiRequestName: string; storyId: string; cardId: string }) =>
		track('onApiRetry', {
			type: 'event',
			action: `retry ${params.storyId}/${params.cardId}/${params.apiRequestName}`,
			payload: {
				event_category: 'Retry to api',
				event_label: params.apiRequestName,
				api_endpoint: params.apiRequestName,
				api_attempt: params.attemptNumber,
				story_id: params.storyId,
				card_id: params.cardId,
				send_to: window.STORYCARDS_GA_ID,
			},
		}),

	onApiError: (params: { errorCode: string | number; apiRequestName: string; storyId: string; cardId: string }) =>
		track('onApiError', {
			type: 'event',
			action: `error ${params.storyId}/${params.cardId}/${params.errorCode}/${params.apiRequestName}`,
			payload: {
				event_category: 'Error from api',
				event_label: `${params.errorCode}`,
				api_endpoint: params.apiRequestName,
				api_error: `${params.errorCode}`,
				story_id: params.storyId,
				card_id: params.cardId,
				send_to: window.STORYCARDS_GA_ID,
			},
		}),

	onOffline: (storyId: string, cardId: string, requestRoute: string) =>
		track('onOffline', {
			type: 'event',
			action: `offline ${storyId}/${cardId}/${requestRoute}`,
			payload: {
				event_category: 'offline mode no session',
				event_label: requestRoute,
				story_id: storyId,
				card_id: cardId,
				send_to: window.STORYCARDS_GA_ID,
			},
		}),

	onSendAnswer: (storyId: string, cardId: string, answerId: string, answerText: string) =>
		track('onSendAnswer', {
			type: 'event',
			action: `answer ${storyId}/${cardId}/${answerId}`,
			payload: {
				event_category: 'Send answer',
				event_label: `answer ${answerId} ${answerText}`,
				story_id: storyId,
				card_id: cardId,
				answer_id: answerId,
				answer_text: answerText,
			},
		}),

	onSearchPerformed: (params: { storyId: string; cardId: string; layerName: string }) =>
		track('onSearchPerformed', {
			type: 'event',
			action: `search ${params.storyId}/${params.cardId}/${params.layerName}`,
			payload: {
				event_category: 'Search performed',
				story_id: params.storyId,
				card_id: params.cardId,
				layer_name: params.layerName,
			},
		}),

	onSearchQuery: (params: { storyId: string; cardId: string; layerName: string; searchTerm: string }) =>
		track('onSearchQuery', {
			type: 'event',
			action: `query ${params.storyId}/${params.cardId}/${params.layerName}/${params.searchTerm}`,
			payload: {
				event_category: 'Search query entered',
				story_id: params.storyId,
				card_id: params.cardId,
				layer_name: params.layerName,
				search_term: params.searchTerm,
			},
		}),

	onVideoPlay: (params: {
		storyId: string;
		cardId: string;
		layerName: string;
		mediaUrl: string;
		mediaDuration: number;
		mediaContentId?: string;
		mediaKind?: MediaAnalyticsKind;
	}) => {
		const kind = params.mediaKind ?? 'video';
		const { noun, eventCategory } = mediaAnalyticsLabels(kind);
		const eventName = kind === 'audio' ? 'onAudioPlay' : 'onVideoPlay';
		const prefix = kind === 'audio' ? 'aplay' : 'vplay';

		return track(eventName, {
			type: 'event',
			action: `${prefix} ${params.storyId}/${params.cardId}/${params.layerName}`,
			payload: pickValid({
				event_category: eventCategory,
				story_id: params.storyId,
				card_id: params.cardId,
				layer_name: params.layerName,
				[`${noun}_url`]: params.mediaUrl,
				[`${noun}_duration`]: params.mediaDuration,
				[`${noun}_content_id`]: params.mediaContentId,
			}),
		});
	},

	onVideoProgress: (params: {
		storyId: string;
		cardId: string;
		layerName: string;
		mediaUrl: string;
		mediaDuration: number;
		mediaProgress: number;
		mediaContentId?: string;
		mediaKind?: MediaAnalyticsKind;
	}) => {
		const kind = params.mediaKind ?? 'video';
		const { noun, eventCategory } = mediaAnalyticsLabels(kind);
		const eventName = kind === 'audio' ? 'onAudioProgress' : 'onVideoProgress';
		const prefix = kind === 'audio' ? 'aprog' : 'vprog';

		return track(eventName, {
			type: 'event',
			action: `${prefix} ${params.storyId}/${params.cardId}/${params.layerName}/${params.mediaProgress}`,
			payload: pickValid({
				event_category: eventCategory,
				story_id: params.storyId,
				card_id: params.cardId,
				layer_name: params.layerName,
				[`${noun}_url`]: params.mediaUrl,
				[`${noun}_duration`]: params.mediaDuration,
				[`${noun}_progress`]: params.mediaProgress,
				[`${noun}_content_id`]: params.mediaContentId,
			}),
		});
	},

	onVideoComplete: (params: {
		storyId: string;
		cardId: string;
		layerName: string;
		mediaUrl: string;
		mediaDuration: number;
		mediaContentId?: string;
		mediaKind?: MediaAnalyticsKind;
	}) => {
		const kind = params.mediaKind ?? 'video';
		const { noun, eventCategory } = mediaAnalyticsLabels(kind);
		const eventName = kind === 'audio' ? 'onAudioComplete' : 'onVideoComplete';
		const prefix = kind === 'audio' ? 'acomp' : 'vcomp';

		return track(eventName, {
			type: 'event',
			action: `${prefix} ${params.storyId}/${params.cardId}/${params.layerName}`,
			payload: pickValid({
				event_category: eventCategory,
				story_id: params.storyId,
				card_id: params.cardId,
				layer_name: params.layerName,
				[`${noun}_url`]: params.mediaUrl,
				[`${noun}_duration`]: params.mediaDuration,
				[`${noun}_content_id`]: params.mediaContentId,
			}),
		});
	},

	onMediaEngaged3s: (params: {
		storyId: string;
		cardId: string;
		layerName: string;
		mediaUrl: string;
		mediaDuration: number;
		mediaContentId?: string;
		mediaKind?: MediaAnalyticsKind;
	}) => {
		const kind = params.mediaKind ?? 'video';
		const { noun, eventCategory } = mediaAnalyticsLabels(kind);
		const eventName = kind === 'audio' ? 'onAudioEngaged3s' : 'onVideoEngaged3s';
		const prefix = kind === 'audio' ? 'aeng3s' : 'veng3s';

		return track(eventName, {
			type: 'event',
			action: `${prefix} ${params.storyId}/${params.cardId}/${params.layerName}`,
			payload: pickValid({
				event_category: eventCategory,
				story_id: params.storyId,
				card_id: params.cardId,
				layer_name: params.layerName,
				[`${noun}_url`]: params.mediaUrl,
				[`${noun}_duration`]: params.mediaDuration,
				[`${noun}_content_id`]: params.mediaContentId,
			}),
		});
	},

	onCustomEvent: ({
		gaParamsStr,
		storyName,
		cardName,
		linkUrl,
	}: {
		gaParamsStr: string;
		storyName?: string;
		cardName?: string;
		linkUrl?: string;
	}) => {
		const gaParams = parseGAParams(gaParamsStr);
		const context = {
			storyName: storyName ?? '',
			cardName: cardName ?? '',
			linkUrl: linkUrl ?? '',
		};

		if (!gaParams.actionName) {
			return;
		}

		// Resolve placeholders (CUSTOM_TRACKER_OPTIONS) in each parameter value
		const resolvedPayload = gaParams.params.reduce(
			(acc, param) => {
				acc[param.name] = resolveCustomTrackerVariables(param.value, context);
				return acc;
			},
			{} as Record<string, string>
		);

		track('onCustomEvent', {
			type: 'event',
			action: gaParams.actionName,
			payload: resolvedPayload,
		});
	},
};

let isFirstPageView = true;

/**
 * @param props.location The page's URL.
 * @param props.path The path portion of location. This value must start with a slash (/) character.
 * @param props.title The page's title.
 * @param props.send_to GA_MEASUREMENT_ID
 * @info https://developers.google.com/analytics/devguides/collection/gtagjs/pages
 */
export const pageview = async (props: { location: string; path: string; title: string; gaUniversalId?: string }) => {
	if (isPreview || !window.gtag) return;

	const { gaUniversalId, ...validParams } = pickValid({
		page_path: props.path,
		page_title: props.title,
		page_location: props.location,
		gaUniversalId: props.gaUniversalId,
	});

	// Setting properties globally to ensure GA uses these values consistently.
	// This prevents GA from using properties from a previous event if not provided.
	if (validParams.page_path) window.gtag('set', 'page_path', validParams.page_path);
	if (validParams.page_title) window.gtag('set', 'page_title', validParams.page_title);
	if (validParams.page_location) window.gtag('set', 'page_location', validParams.page_location);

	log('pageview', validParams);

	if (isFirstPageView) {
		// Initial 'page_view' event doesn't send automatically by default (see `{ send_page_view: false }`)
		await trackPageView({ type: 'event', action: 'page_view', payload: validParams });
		isFirstPageView = false;
	} else if (gaUniversalId) {
		// GA4 sends 'page_view' events automatically by default, while GA3 (Universal) requires manual sending.
		await trackPageView({
			type: 'event',
			action: 'page_view',
			payload: { ...validParams, send_to: gaUniversalId },
		});
	}

	async function trackPageView(params: GAEvent) {
		if (!window.gtag) {
			return;
		}

		window.gtag(params.type, params.action, params.payload);
		await gaBridge.sendEvent({ type: 'page_view', details: params });
	}
};
